import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

const QuadreMedicAutocomplete = ({
  items,
  getItemValue,
  value,
  onChange,
  onSelect,
  shouldItemRender,
  renderItem,
  inputId,
  inputRef,
}) => {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);

  const visibleItems = useMemo(
    () =>
      (items || []).filter((item) =>
        shouldItemRender ? shouldItemRender(item, value) : true,
      ),
    [items, shouldItemRender, value],
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [value, visibleItems.length]);

  const selectItem = (item) => {
    const selectedValue = getItemValue(item);
    onSelect(selectedValue, item);
    setOpen(false);
  };

  const handleBlur = (event) => {
    if (wrapperRef.current?.contains(event.relatedTarget)) {
      return;
    }
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }

    if (!open || visibleItems.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((index) =>
        index + 1 >= visibleItems.length ? 0 : index + 1,
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((index) =>
        index - 1 < 0 ? visibleItems.length - 1 : index - 1,
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      selectItem(visibleItems[highlightedIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef}>
      <input
        id={inputId}
        ref={inputRef}
        type="text"
        value={value}
        onChange={(event) => {
          onChange(event, event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {open && visibleItems.length > 0 ? (
        <div role="listbox">
          {visibleItems.map((item, index) => (
            <div
              key={getItemValue(item)}
              role="option"
              aria-selected={index === highlightedIndex}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => selectItem(item)}
            >
              {renderItem(item, index === highlightedIndex)}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

QuadreMedicAutocomplete.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  getItemValue: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  shouldItemRender: PropTypes.func,
  renderItem: PropTypes.func.isRequired,
  inputId: PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};

export default QuadreMedicAutocomplete;
